import React, { useState, useEffect } from 'react';
import { RefreshCw, Database, Headphones, Smartphone, ArrowRight, CheckCircle2, Calendar, Play, Globe, Users, Clock, Star, Target, Eye, Quote, Shield, TrendingUp, DollarSign, Zap } from 'lucide-react';

const challenges = [
  {
    icon: RefreshCw,
    title: 'Manual, repetitive tasks',
    description: 'Manual processes consume valuable resources and stifle innovation. We automate workflows to free your teams for strategic work.',
    stats: ['45% time wasted', '3.5 hours/day lost', '$150k annual cost'],
  },
  {
    icon: Database,
    title: "Your company's knowledge is siloed",
    description: 'Critical information is scattered across systems, causing delays and redundant work. Our AI Knowledge Hub centralizes your collective intelligence.',
    stats: ['6 months onboarding', '30% knowledge loss', '2x support tickets'],
  },
  {
    icon: Headphones,
    title: 'Slow responses, costly scaling',
    description: 'Slow response times lose customers and scaling through hiring is expensive. Our AI Agents provide instant, intelligent interactions 24/7.',
    stats: ['4hr response time', '25% customer churn', '$200k hiring costs'],
  },
  {
    icon: Smartphone,
    title: "Your products aren't keeping up",
    description: 'Products without AI risk obsolescence. We embed advanced AI into your products to ensure market leadership.',
    stats: ['15% market share loss', '30% feature gap', '2x competitor growth'],
  },
];

const dataChallenges = [
  {
    icon: Shield,
    title: 'Siloed Data?',
    description: 'Secure ETL pipelines handle petabytes with industry-leading precision. From raw ingestion to predictive models, we deliver clean, reliable data wherever you need it.',
    stat: '99.9% Data Accuracy',
  },
  {
    icon: TrendingUp,
    title: 'Slow Decisions?',
    description: 'Real-time BI dashboards in Power BI, Tableau, or custom web apps provide instant visibility for faster, smarter decision-making across your organization.',
    stat: '80% Faster Reporting',
  },
  {
    icon: DollarSign,
    title: 'Rising Costs?',
    description: 'Infrastructure-as-code on AWS, Azure, and GCP optimizes your cloud environment for maximum efficiency and substantial cost reductions.',
    stat: 'Average 30% Cloud Savings',
  },
];

const testimonials = [
  {
    quote: "I had the pleasure of working with Agility on Data Warehousing and Automation projects. They consistently deliver exceptional results and demonstrate deep technical expertise. Their innovative solutions and dedication to quality have greatly benefited our projects. I highly recommend Agility for any data-driven initiatives.",
    initials: "MJ",
    name: "Manjit Jhooty",
    title: "CEO, Jhoots Group",
    image: "https://cdn.theorg.com/db3098f3-4457-4e13-ba82-94fc84878dce_thumb.jpg",
  },
  {
    quote: "Working with Agility was a game-changer. They didn't just meet deadlines they anticipated problems before they arose and solved them efficiently. Their ability to operate autonomously while maintaining exceptional quality made them an invaluable partner for our projects.",
    initials: "LW",
    name: "Lindsay Williams",
    title: "CEO, FoodDrop",
    image: "https://lh3.googleusercontent.com/a-/ALV-UjW0CAQs5vi1mYZ6_yFemjiL1AJ0VdZDKkFr-ok6L9_sVghrcgtHMlAGnHeLoYySwPU5bIs8qBe6_pAwdOXQw2x4F0H0eEbJ9jZpvYKMI3JM4aYa6GkkxAZr07SEv23EJl0m9BiT5xC6NABOWGEtDf9DyR8idaM_YFmuyH4-n-iZaXilQ_jEY7b3hmcTL_Tqi36L1XZjCiyHiCluNczhFThsHGEPFVEbXe2iApG8QNkra96FAS9NO2zvQqvyo2xVuc598QL5V1cOn2NT-ZzEL1vL9wBL7yAJUnzubC1t8ZmU13Ak6jmhvTSaHQ9oNCvL875qgl3LT0hqqUEjXqTKBucOzfRriqJpF3qlhR2Kkoe4fkTAaL4hivgIBIZTEVjHg-dDqg_mSNYLaC8_NYGEiuxO5iUlyJwd0SXr0KZdbdMQPzsetqjkyzaYpVNSYHoIV79Pr72fZaQOm0Ou_GDir3dkVcM9ZQoWjZIWdyUnECfJcQyRdeZLlpfZtktU_aXCU6iglpJpbQaujzv74WxoR0FDxbW-jGp33nR_R9Bl36FuKTJtd0ZCEXBdeoQAgPLDsKBtyf2Eli1Qr7_QZQXxJrtGbASEfdmQc82QN8VMdLiVYCUUddIr5XWJ6jfgsLiYxVIZTCL92h9IZv8v4_ZqPsQN4J8WsEd7U8TeHVhy9tXRnG0f86iPoVUj5twcDxqwOzltHQQFSEQg3kp2-hu21qGZHE2QOCDKXgoZJ9yF5M6l8buS11mB1mlLSr01VvzbaPvZr-oIc3hUyXhCvvFgMFoMQAAGmmIpbquuioIunEsatjoJGuYZHOEBduEI8FqxtyAkgzAs7sJTEZ--5VpPDbBZTs0gtaNzos1V3I-1tsVDEXZhz6NWbW0vsWr92dgQFcCBPEi5soHOn6BbPMJpZGYTzhon-xTKkS6J-NudjGJKHrBYokptCH_bm6-cAlTkwAw8QmGK_mo4hTPT6QuGwVAdxxfVTG0avxox4yNcdH2fKMZycqKh0tBRddhOSqvS3MJUvu1rMt70Ik418DKUaPG6xJj1Xok9W-8uIuSf3Pg9Ol0z69TaVQ=s240-p-k-rw-no",
  },
];

const methodologySteps = [
  {
    number: '1',
    title: 'Discovery & Assessment',
    description: 'Comprehensive technical evaluation of current systems, data infrastructure, and business objectives to develop targeted solutions aligned with strategic goals.',
  },
  {
    number: '2',
    title: 'Solution Architecture',
    description: 'Design scalable, production-ready architecture leveraging proven technologies. Development of proof-of-concept demonstrating feasibility and expected outcomes.',
  },
  {
    number: '3',
    title: 'Agile Implementation',
    description: 'Iterative development with continuous validation, ensuring alignment with requirements. Deployment to production environments with comprehensive testing protocols.',
  },
  {
    number: '4',
    title: 'Optimization & Support',
    description: 'Continuous performance monitoring, optimization based on operational metrics, and ongoing technical support ensuring sustained value delivery.',
  },
];

const stats = [
  {
    icon: Globe,
    number: '30+',
    label: 'Countries Served',
  },
  {
    icon: Users,
    number: '250+',
    label: 'Tech Partners',
  },
  {
    icon: Clock,
    number: '24/7',
    label: 'Global Support',
  },
  {
    icon: Star,
    number: '5⭐',
    label: 'Clutch Rating',
  },
];

const BusinessChallenges = () => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      {/* New Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 transition-all duration-1000 ease-out-expo fade-in-up ${
            showContent ? 'is-visible' : ''
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Accelerating Your <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Data-Powered Future</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto mb-8">
              We design, build, and optimize intelligent data solutions from AI-driven insights to automated pipelines so you can outpace competitors with smarter, faster decisions. Join leaders in finance, healthcare, and retail slashing costs 30-70% and boosting accuracy to 88%.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-teal-400 hover:to-cyan-400 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Data Maturity Audit
              </a>
              <a
                href="/solutions"
                className="inline-flex items-center px-8 py-4 bg-transparent text-white font-semibold rounded-lg border-2 border-gray-700 hover:border-teal-400 hover:text-teal-400 transition-all duration-300 transform hover:scale-105"
              >
                <Play className="w-5 h-5 mr-2" />
                Watch Solutions Demo
              </a>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="relative bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8
                  shadow-lg hover:shadow-xl border border-gray-700/50
                  transition-all duration-300 transform hover:-translate-y-1 group
                  hover:bg-gray-800/70 card-hover fade-in-up text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-400
                  rounded-2xl flex items-center justify-center mb-4 mx-auto transform transition-transform
                  duration-300 group-hover:scale-110">
                  <stat.icon className="h-8 w-8 text-white" />
                </div>
                <div className="text-4xl font-bold text-white mb-2 bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  {stat.number}
                </div>
                <div className="text-gray-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are Section - Part 1 */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Badge */}
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full text-teal-400 text-sm font-semibold">
              Who We Are
            </span>
          </div>

          <div className={`text-center mb-16 transition-all duration-1000 ease-out-expo fade-in-up ${
            showContent ? 'is-visible' : ''
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              We Solve Your Biggest Data Challenges
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              At Agility, our tight-knit collective of data engineers, cloud architects, and AI specialists transforms raw chaos into strategic advantage. We partner to build adaptive solutions for evolving businesses just like we did for Healthcare Solutions Inc., cutting manual processing 70%.
            </p>
          </div>

          {/* 3 Data Challenge Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {dataChallenges.map((challenge, index) => (
              <div
                key={index}
                className="relative bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8
                  shadow-lg hover:shadow-xl border border-gray-800/50
                  transition-all duration-300 transform hover:-translate-y-1 group
                  hover:bg-gray-900/70 card-hover fade-in-up"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-400
                  rounded-2xl flex items-center justify-center mb-6 transform transition-transform
                  duration-300 group-hover:rotate-12 group-hover:scale-110">
                  <challenge.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{challenge.title}</h3>
                <p className="text-gray-400 mb-6">{challenge.description}</p>
                <div className="inline-block px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg text-white font-semibold text-sm">
                  {challenge.stat}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission, Vision & Team Section - Part 2 */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Badge */}
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full text-teal-400 text-sm font-semibold">
              Our Purpose
            </span>
          </div>

          <div className={`text-center mb-6 transition-all duration-1000 ease-out-expo fade-in-up ${
            showContent ? 'is-visible' : ''
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Mission, Vision & Team
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-12">
              Driving innovation and delivering transformative results for our clients worldwide
            </p>
          </div>

          {/* Mission, Vision & Team Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Mission Card */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 shadow-lg hover:shadow-xl hover:border-teal-500/30 transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-400 rounded-2xl
                flex items-center justify-center mb-6 transform transition-transform duration-300 hover:scale-110 hover:rotate-6">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-gray-300 leading-relaxed">
                To empower organizations with end-to-end data solutions from secure ingestion and cleansing to advanced, automated analytics so every decision is backed by real insight.
              </p>
            </div>

            {/* Vision Card */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 shadow-lg hover:shadow-xl hover:border-teal-500/30 transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-400 rounded-2xl
                flex items-center justify-center mb-6 transform transition-transform duration-300 hover:scale-110 hover:rotate-6">
                <Eye className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-gray-300 leading-relaxed">
                To create real-world impact with our solutions, that could be AI, data or automation.
              </p>
            </div>

            {/* Team Card */}
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 shadow-lg hover:shadow-xl hover:border-teal-500/30 transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-400 rounded-2xl
                flex items-center justify-center mb-6 transform transition-transform duration-300 hover:scale-110 hover:rotate-6">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Our Team</h3>
              <p className="text-gray-300 leading-relaxed">
                A dynamic team averaging 24 years old, combining fresh perspectives with proven expertise. We move faster, iterate quicker, and ship solutions at the speed of modern business not bureaucracy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Methodology Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Badge */}
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full text-teal-400 text-sm font-semibold">
              Our Methodology
            </span>
          </div>

          <div className={`text-center mb-16 transition-all duration-1000 ease-out-expo fade-in-up ${
            showContent ? 'is-visible' : ''
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Proven Implementation Framework
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              A structured approach combining technical expertise with agile methodology to deliver rapid, measurable results for enterprise clients.
            </p>
          </div>

          {/* 4 Methodology Steps in 2x2 Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {methodologySteps.map((step, index) => (
              <div
                key={index}
                className="relative bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-800/50
                  shadow-lg hover:shadow-xl hover:border-teal-500/30 transition-all duration-300 transform hover:-translate-y-1 group"
              >
                {/* Large Number */}
                <div className="absolute top-6 right-6 text-7xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent opacity-20">
                  {step.number}
                </div>

                {/* Small Number Badge */}
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-400 rounded-xl mb-6 relative z-10">
                  <span className="text-white text-xl font-bold">{step.number}</span>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 relative z-10">{step.title}</h3>
                <p className="text-gray-300 leading-relaxed relative z-10">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Success Stories Section - Part 3 */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Badge */}
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full text-teal-400 text-sm font-semibold">
              Client Success Stories
            </span>
          </div>

          <div className={`text-center mb-12 transition-all duration-1000 ease-out-expo fade-in-up ${
            showContent ? 'is-visible' : ''
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Proven Results That Speak Volumes
            </h2>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-12">
              Don't just take our word for it see what our clients have achieved with our data solutions
            </p>
          </div>

          {/* Testimonial Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50
                  shadow-lg hover:shadow-xl transition-all duration-300 relative"
              >
                {/* Large Quote Icon */}
                <Quote className="absolute top-6 left-6 h-12 w-12 text-teal-500/20" />

                <div className="relative z-10 mt-8">
                  <p className="text-gray-300 text-lg leading-relaxed mb-8 italic">
                    "{testimonial.quote}"
                  </p>

                  <div className="flex items-center">
                    {/* Avatar Image */}
                    <img
                      src={testimonial.image}
                      alt={`${testimonial.name}, ${testimonial.title} - Client Testimonial`}
                      className="w-14 h-14 rounded-full object-cover mr-4 border-2 border-teal-500/50"
                    />

                    <div>
                      <div className="text-white font-semibold text-lg">{testimonial.name}</div>
                      <div className="text-teal-400 text-sm">{testimonial.title}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default BusinessChallenges;